import {Component, Input, OnInit, OnDestroy, HostListener} from '@angular/core';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {BatchTransactionsService} from 'src/app/services/batchTransactions.service';
import BatchTransactionsModel from 'src/app/services/batchTransactions.model';
import {PondSectionService} from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {FeedLogService} from 'src/app/services/feedLog.service';
import FeedLogModel from 'src/app/services/feedLog.model';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import * as moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @component BatchTransactionsComponent
 * @selector app-batch-transactions
 * @description Display and manage batch feed transaction entries for aquaculture farm sections.
 *
 * This component provides a list view interface for viewing historical batch feed transactions
 * with infinite scroll pagination, meal/feeding schedule details, and transaction-specific information.
 * It retrieves batch transactions for a selected farm section and displays related feed logs,
 * meal entries, and feeding history with drill-down capabilities.
 *
 * Key Features:
 * 1. Batch Transaction List - Display paginated list of feed batch transactions with infinite scroll
 * 2. Hierarchical Location Display - Shows business entity, farm site, and section context
 * 3. Meal Entry Details - Drill-down view showing meal entries and feeding schedules per transaction
 * 4. Infinite Scroll Pagination - Dynamically load transactions as user scrolls (12 items per batch)
 * 5. Meal Aggregation - Group feed meals by date and meal number with hierarchy tracking
 * 6. Check Tray Integration - Associate check tray readings with specific meal entries
 * 7. Feed Log Association - Link feed log data to meal entries for reference
 * 8. User Context Display - Show logged-in user name and assigned business name
 * 9. Filtering & Search - Support filter state for search-based transaction lists
 * 10. Responsive Loading - Show/hide loading spinners during data operations
 *
 * Architecture:
 * - Hierarchical Data: Batch Transaction → Pond → Feed Meal Entry → Feed Type → Check Tray/Feed Log
 * - Pagination: Uses BatchLoader utility for infinite scroll with configurable batch size
 * - Data Models: Leverages BatchTransactionsModel, FeedLogModel for type safety
 * - State Management: Manages filter state, pagination state, and modal visibility
 * - User Context: Retrieves login person type, user name, business name from localStorage
 *
 * Role-Based Behavior:
 * - All users can view batch transactions for assigned section
 * - Admin users can view transactions across different business entities
 * - Farmer users see only transactions for their assigned business
 *
 * Data Flow:
 * 1. Component initialization loads batch transactions for section via getFeedBatchTransactions()
 * 2. BatchLoader utility handles pagination state and infinite scroll callbacks
 * 3. User scrolls to bottom → checkScrollAndLoad() triggers loading next batch
 * 4. User clicks on transaction → getBatchEntryDetailsByBatchNumber() loads meal details
 * 5. Feed logs enriched with meal hierarchy: meal number, date grouping, check tray data
 * 6. Modal displays meal entries grouped by pond with feeding schedule details
 * 7. User can convert units (grams to kgs) and format times for display
 *
 * @implements OnInit, OnDestroy
 * @selector app-batch-transactions
 * @templateUrl ./batch-transactions.component.html
 * @styleUrls ./batch-transactions.component.scss
 *
 * @service ToastrService - Display success/warning/error notifications
 * @service FeedLogService - Fetch feed logs for batch transactions
 * @service CustomerPondService - Load pond details (currently unused, placeholder)
 * @service PondSectionService - Retrieve section metadata (currently unused, placeholder)
 * @service BatchTransactionsService - Fetch batch transaction list and details
 * @service FarmSiteService - Load farm site metadata (currently unused, placeholder)
 * @service BusinessEntityService - Load business entity data (currently unused, placeholder)
 * @service CommonMethodsService - Handle error messages and common operations
 *
 * @example
 * // Component usage in parent component
 * <app-batch-transactions
 *   [businessId]="selectedBusinessId"
 *   [siteId]="selectedSiteId"
 *   [sectionId]="selectedSectionId">
 * </app-batch-transactions>
 *
 * Data Flow Example:
 * Component Load → getFeedBatchTransactions(businessId, siteId, sectionId)
 *   ├─ Fetch batch transactions from BatchTransactionsService
 *   ├─ Initialize BatchLoader for pagination
 *   └─ Load first batch of 12 transactions
 *
 * User Scrolls to Bottom → checkScrollAndLoad()
 *   ├─ Check scroll position (100px threshold)
 *   ├─ Verify BatchLoader not already loading
 *   ├─ Call loadNextBatch() to fetch next 12 transactions
 *   └─ Update batchTransactionsList with new entries
 *
 * User Clicks Transaction → getBatchEntryDetailsByBatchNumber(batchNumber, dateTime)
 *   ├─ Fetch feed logs for batch from FeedLogService
 *   ├─ Transform: flatMap feedMeal to MealFeedType array
 *   ├─ Enrich: Add check tray and feed log objects to each meal
 *   ├─ Aggregate: Group meals by date and meal number
 *   ├─ Calculate: Meal sequence numbers based on date and meal ID
 *   └─ Display: Show modal with structured meal hierarchy
 */
@Component({
  selector: 'app-batch-transactions',
  templateUrl: './batch-transactions.component.html',
  styleUrls: ['./batch-transactions.component.scss']
})
export class BatchTransactionsComponent implements OnInit, OnDestroy {

  /**
   * @type {Array<BusinessEntity>}
   * @description Business entities available to logged-in user
   * @default []
   * @side-effect Populated by BusinessEntityService (currently not used in this component)
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * @type {Array<FarmSiteModel>}
   * @description Farm sites for selected business entity
   * @default []
   * @side-effect Populated by FarmSiteService (currently not used in this component)
   */
  public farmSites: Array<FarmSiteModel>;

  /**
   * @type {Array<BatchTransactionsModel>}
   * @description Complete list of all batch transactions for selected section
   * @default []
   * @side-effect Used as source data by BatchLoader for pagination
   */
  public batchTransactions: Array<BatchTransactionsModel>;

  /**
   * @type {Array<BatchTransactionsModel>}
   * @description Paginated list of batch transactions currently displayed to user
   * @default []
   * @side-effect Updated by BatchLoader as user scrolls
   */
  public batchTransactionsList: Array<BatchTransactionsModel>;

  /**
   * @type {BatchTransactionsModel}
   * @description Single batch transaction model instance
   * @side-effect Used for individual transaction operations
   */
  public batchTransaction: BatchTransactionsModel;

  /**
   * @type {Array<PondSectionModel>}
   * @description Pond sections for selected farm site
   * @default []
   * @side-effect Populated by PondSectionService (currently not used in this component)
   */
  public pondSections: Array<PondSectionModel>;

  /**
   * @type {Array<CustomerPondModel>}
   * @description Ponds for selected section
   * @default []
   * @side-effect Populated by CustomerPondService (currently not used in this component)
   */
  public customerPonds: Array<CustomerPondModel>;

  /**
   * @type {Array<FeedLogModel>}
   * @description Feed logs for currently selected batch transaction
   * @side-effect Populated by getBatchEntryDetailsByBatchNumber; transformed with meal hierarchy
   */
  public feedLogs: Array<FeedLogModel>;

  /**
   * @type {FeedLogModel}
   * @description Single feed log model instance
   * @side-effect Used for individual feed log operations
   */
  public feedLog: FeedLogModel;

  /**
   * @type {string}
   * @description Login person type from localStorage
   * @default localStorage.getItem('loginPersonType')
   * @side-effect Used to determine user role (Admin/Farmer/etc)
   */
  loginPersonType: string;

  /**
   * @type {string}
   * @description Logged-in user's username from localStorage
   * @default localStorage.getItem('LoginUserName')
   * @side-effect Displayed in UI header for user context
   */
  LoginUserName: string;

  /**
   * @type {string}
   * @description Logged-in user's assigned business name from localStorage
   * @default localStorage.getItem('loginBusinessName')
   * @side-effect Displayed in UI to show user's business context
   */
  loginUserBusinessName: string;

  /**
   * @type {boolean}
   * @description Show/hide loading spinner while fetching batch transactions from API
   * @default false
   * @side-effect Set during async operations (getFeedBatchTransactions)
   */
  public responseLoader: boolean;

  /**
   * @type {string}
   * @description Current farm site ID filter
   * @default ''
   * @side-effect Used to filter batch transactions
   */
  public farm_site_id: string;

  /**
   * @type {string}
   * @description Current business ID filter
   * @default ''
   * @side-effect Used to filter batch transactions
   */
  public business_id: string;

  /**
   * @type {any}
   * @description Current section ID filter
   * @default undefined
   * @side-effect Used to filter batch transactions by section
   */
  public section_id: any;

  /**
   * @type {Array}
   * @default []
   * @description Detailed batch information array
   * @side-effect Used to display batch details in modal
   */
  public batchDetails = new Array();

  /**
   * @type {any}
   * @description Feed logs data for currently selected batch
   * @side-effect Populated by getBatchEntryDetailsByBatchNumber; passed to modal for display
   */
  public feedLogsData: any;

  /**
   * @type {any}
   * @description Temporary/placeholder variable
   * @deprecated Not actively used in component
   */
  abc: any;

  /**
   * @type {number}
   * @default 1
   * @description Current page number for pagination display
   */
  currentPageNumber = 1;

  /**
   * @type {number}
   * @default 20
   * @description Number of items per page for pagination
   */
  noOfItemsPerPage = 20;

  /**
   * @type {boolean}
   * @description Flag indicating if search/filter was clicked
   * @default false
   * @side-effect Set by handleReadingsTable() to toggle search state
   */
  isSearchClicked: boolean;

  /**
   * @type {any}
   * @description Business ID parameter from parent component @Input
   * @side-effect Used in getFeedBatchTransactions
   */
  paramsbusinessId: any;

  /**
   * @type {any}
   * @description Farm site ID parameter from parent component @Input
   * @side-effect Used in getFeedBatchTransactions
   */
  paramsfarmsiteId: any;

  /**
   * @type {any}
   * @description Section ID parameter from parent component @Input
   * @side-effect Used in getFeedBatchTransactions
   */
  paramssectionId: any;

  /**
   * @type {any}
   * @description Current date formatted as "DD-MMM-YYYY"
   * @default moment(new Date()).format('DD-MMM-YYYY')
   * @side-effect Used for date display in UI
   */
  currentDate: any;

  /**
   * @type {string}
   * @Input
   * @description Business ID passed from parent component
   * Used to identify user's business context for authorization
   */
  @Input('businessId') public businessId: string;

  /**
   * @type {string}
   * @Input
   * @description Farm site ID for which batch transactions are displayed
   * Identifies specific aquaculture farm location
   */
  @Input('siteId') public siteId: string;

  /**
   * @type {string}
   * @Input
   * @description Section ID within farm site
   * Used to filter batch transactions for specific pond section
   */
  @Input('sectionId') public sectionId: string;

  /**
   * @type {any}
   * @description Pond details for currently selected batch transaction
   * @side-effect Populated during batch entry detail retrieval
   */
  public pondDetails: any;

  /**
   * @type {any}
   * @description Meal details for currently selected batch transaction
   * @side-effect Populated during batch entry detail retrieval
   */
  public mealDetails: any;

  /**
   * @type {any}
   * @description Selected batch date/time stamp
   * @side-effect Set by getBatchEntryDetailsByBatchNumber for display context
   */
  selectedBatchTimeStamp: any;

  /**
   * @type {number}
   * @description Active page number state for pagination component binding
   * @side-effect Set by pageChange() method
   */
  public activePageNo: number;

  /**
   * @type {boolean}
   * @default false
   * @description Show/hide loading spinner while fetching paginated batch data
   * @side-effect Set by BatchLoader callback during loadNextBatch
   */
  public listLoader = false;

  /**
   * @type {number}
   * @default 12
   * @description Number of records to load per batch during infinite scroll
   */
  public recordsPerBatch = 12;

  /**
   * @type {number}
   * @default 0
   * @description Current index for records loaded so far
   * Tracks pagination progress through total list
   */
  public startIndex = 0;

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if list is currently filtered (search/filter mode)
   * When true, prevents loading more items until filter is cleared
   */
  public isFiltering: boolean = false;

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if no batch transactions exist for current section
   * Set by BatchLoader when filtered/loaded list is empty
   */
  public noList: boolean = false;

  /**
   * @type {BatchLoader<any>}
   * @private
   * @description Infinite scroll pagination utility instance
   * Manages batch loading, state, and callback execution
   * @side-effect Initialized in initializeBatchLoader(), used in onScroll and checkScrollAndLoad
   */
  private batchLoader: BatchLoader<any>;

  /**
   * @method constructor
   * @description Initialize component with required service dependencies
   *
   * @service ToastrService - Notification service for displaying alerts to users
   * @service FeedLogService - Service for loading feed logs from API
   * @service CustomerPondService - Service for loading pond details (currently unused placeholder)
   * @service PondSectionService - Service for loading section metadata (currently unused placeholder)
   * @service BatchTransactionsService - Service for retrieving batch transaction list
   * @service FarmSiteService - Service for loading farm site data (currently unused placeholder)
   * @service BusinessEntityService - Service for loading business entity data (currently unused placeholder)
   * @service Sidenavaction - Utility service for side navigation actions
   * @service CommonMethodsService - Service for common utility methods and error handling
   *
   * @param {ToastrService} toasterService - Injectable for displaying notifications
   * @param {FeedLogService} feedLogService - Injectable for feed log data operations
   * @param {CustomerPondService} customerPondService - Injectable for pond data operations
   * @param {PondSectionService} pondSectionService - Injectable for section data operations
   * @param {BatchTransactionsService} batchTransactionService - Injectable for batch transaction operations
   * @param {FarmSiteService} farmSitesService - Injectable for farm site data operations
   * @param {BusinessEntityService} businessEntityService - Injectable for business entity operations
   * @param {Sidenavaction} sidenavaction - Injectable for side navigation functionality
   * @param {CommonMethodsService} commonMethodsService - Injectable for common utility operations
   *
   * @side-effect Services are stored as class properties for use throughout component lifecycle
   */
  constructor(private toasterService: ToastrService, public feedLogService: FeedLogService, public customerPondService: CustomerPondService, public pondSectionService: PondSectionService, public batchTransactionService: BatchTransactionsService, public farmSitesService: FarmSiteService, public businessEntityService: BusinessEntityService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService) {
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description Initialize component state and load batch transactions for section
   *
   * Execution flow:
   * 1. Scroll to top of page (window.scrollTo)
   * 2. Reset all filter and state flags (isFiltering, isSearchClicked, responseLoader)
   * 3. Clear batch transaction arrays (batchTransactions, batchTransactionsList)
   * 4. Retrieve user context from localStorage (loginPersonType, LoginUserName, loginUserBusinessName)
   * 5. Copy input parameters to local variables (paramsbusinessId, paramsfarmsiteId, paramssectionId)
   * 6. Initialize current date formatted as "DD-MMM-YYYY"
   * 7. Load batch transactions via getFeedBatchTransactions()
   *
   * @returns {Promise<void>}
   * @side-effect
   * - Resets all UI state flags and pagination arrays
   * - Retrieves user context from browser localStorage
   * - Triggers async batch data loading from API
   * - Initializes BatchLoader for infinite scroll pagination
   */
  async ngOnInit() {
    window.scrollTo(0, 0);
    this.isFiltering = false;
    this.batchTransactions = [];
    this.batchTransactionsList = [];
    this.loginPersonType = localStorage.getItem('loginPersonType');
    /* user login details */
    this.LoginUserName = localStorage.getItem('LoginUserName');
    this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
    /* user login details end */
    this.responseLoader = true;
    this.isSearchClicked = false; // if data is not available
    this.farm_site_id = '';
    this.business_id = '';
    this.paramsbusinessId = this.businessId;
    this.paramsfarmsiteId = this.siteId;
    this.paramssectionId = this.sectionId;
    this.getFeedBatchTransactions(this.paramsbusinessId, this.paramsfarmsiteId, this.paramssectionId);
    this.currentDate = moment(new Date()).format('DD-MMM-YYYY');
  }

//To cancel ongoing requests and unsubscribe from subscriptions.
  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description Cancel all pending HTTP requests when component is destroyed
   *
   * @returns {void}
   * @side-effect Aborts all in-flight HTTP requests from BatchTransactionsService
   */
  public ngOnDestroy(): void {
    this.batchTransactionService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getFeedBatchTransactions
   * @async
   * @description Load all batch feed transactions for specified business, farm site, and section
   *
   * Execution flow:
   * 1. Clear batch transaction arrays (batchTransactions, batchTransactionsList)
   * 2. Reset filter flag (isFiltering = false)
   * 3. Fetch batch transactions from BatchTransactionsService using IDs
   * 4. Initialize BatchLoader for infinite scroll pagination
   * 5. Reset BatchLoader state
   * 6. Load first batch of transactions (12 records default)
   * 7. Show warning toast if no batch data available
   *
   * @param {any} bid - Business ID to filter transactions
   * @param {any} siteid - Farm site ID to filter transactions
   * @param {any} sectionid - Section ID to filter transactions
   * @returns {Promise<void>}
   * @side-effect
   * - Populates batchTransactions with all transactions for section
   * - Initializes BatchLoader instance and loads first paginated batch
   * - Sets warning toast if no data available
   * - Clears any active filters
   */
  async getFeedBatchTransactions(bid, siteid, sectionid) {
    this.batchTransactions = [];
    this.isFiltering = false;
    try {
      this.batchTransactions = await this.batchTransactionService.getAllBatchTransactionsByIds(bid, siteid, sectionid);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      if (this.batchTransactions.length == 0) {
        this.toasterService.warning('Batch data not available.');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onScroll
   * @hostListener window:scroll
   * @description Detect window scroll events and trigger batch loading on infinite scroll
   *
   * Delegates to checkScrollAndLoad() which implements debounce and scroll position check
   *
   * @returns {void}
   * @side-effect Triggers checkScrollAndLoad method when user scrolls
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @type {any}
   * @private
   * @description Timeout ID for debouncing scroll events
   */
  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Check if user scrolled to bottom and load next batch of transactions
   *
   * Logic:
   * 1. Clear any pending scroll timeout to debounce rapid scroll events (50ms delay)
   * 2. Check if scroll position reached near bottom (100px threshold)
   * 3. Verify BatchLoader is not already loading to prevent duplicate requests
   * 4. Verify not in filtering mode (prevents loading during search)
   * 5. If all conditions met, call loadNextBatch() to fetch next batch of transactions
   *
   * @returns {void}
   * @side-effect
   * - Clears scrollTimeout on each call
   * - Sets new scrollTimeout (50ms debounce)
   * - Calls batchLoader.loadNextBatch() when conditions met
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Create and configure BatchLoader instance for infinite scroll pagination
   *
   * Configuration:
   * - Data source: Returns filtered list if isFiltering=true, else full list
   * - On load callback: Updates batchTransactionsList with paginated transactions
   * - Sets noList flag when paginated list is empty
   * - Loading state callback: Updates listLoader flag to show/hide spinner
   *
   * @returns {void}
   * @side-effect
   * - Initializes batchLoader instance
   * - Sets up callbacks for list updates and loading state
   * - Future calls to reset() or loadNextBatch() will use this configuration
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.batchTransactionsList : this.batchTransactions,
      (updatedList) => {
        this.batchTransactionsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method handleReadingsTable
   * @description Reset search/filter flag to indicate no active search state
   *
   * Called when exiting search mode or clearing filters.
   * Sets isSearchClicked=false to show full batch transaction list.
   *
   * @returns {void}
   * @side-effect Sets isSearchClicked=false to display all data
   */
  handleReadingsTable() {
    this.isSearchClicked = false;
  }

  /**
   * @method getBatchEntryDetailsByBatchNumber
   * @async
   * @description Load feed logs and meal details for selected batch transaction
   *
   * Execution flow:
   * 1. Store selected batch timestamp for display context
   * 2. Fetch feed logs for batch from FeedLogService
   * 3. Transform feedMeal array:
   *    - Use flatMap to expand MealFeedType array
   *    - For each meal type, track meal count and index
   * 4. Enrich each meal type with:
   *    - Matching check tray readings (by feed_meal_id)
   *    - Associated feed log object
   *    - Last meal flag from parent feedType
   *    - Meal sequence number
   * 5. Aggregate meals by date and meal number:
   *    - Group by last_feed_given_date
   *    - Group by meal_id within same date
   *    - Calculate cumulative meal numbers
   * 6. On success: Populate feedLogsData and show modal
   * 7. On error: Show warning toast and hide modal
   *
   * Data structure after transformation:
   * feedLogs[].feedMeal[] → Contains individual meal entries
   * Each meal entry includes: meal number, check tray data, feed log object, last meal flag
   *
   * @param {any} batchNumber - Batch transaction number to load details for
   * @param {any} dateTime - Date/time stamp of batch for display
   * @returns {Promise<void>}
   * @side-effect
   * - Populates feedLogs with enriched meal hierarchy data
   * - Sets feedLogsData for modal display
   * - Shows modal (#myModal) on success or hides modal (#addModal) on empty data
   */
  public async getBatchEntryDetailsByBatchNumber(batchNumber, dateTime) {
    try {
      let mealFeedCount: number = 0;
      this.selectedBatchTimeStamp = dateTime;
      this.feedLogs = await this.feedLogService.getFeedLogsByBatchNumber(batchNumber);
      this.feedLogs.forEach((item: any) => {
        mealFeedCount = 0;
        item.feedMeal = item.feedMeal.flatMap((feedType: any) => {
          mealFeedCount++;
          feedType.MealFeedType.forEach((mealType: any) => {
            const checkTrayObject = feedType.checkTray.find((obj: any) => obj.feed_meal_id === mealType.meal_id);
            const feedLogObject = feedType.feedLog;
            mealType.checkTray = checkTrayObject;
            mealType.feedLogObject = feedLogObject;
            mealType.last_meal = feedType.last_meal;
            mealType.meal = mealFeedCount;
          });
          return feedType.MealFeedType;
        });
      });

      this.feedLogs.forEach((item: any) => {
        let currentMeal = 1;
        let previousDate = null;
        let mealId = null
        item.feedMeal.forEach((feed: any) => {
          if (feed.feedLogObject.last_feed_given_date == previousDate || previousDate == null) {
            if (feed.meal_id == mealId || (feed.meal_id != mealId && mealId == null) ) {
              previousDate = feed.feedLogObject.last_feed_given_date;
              mealId = feed.meal_id
              feed.meal = currentMeal;
            } else {
              previousDate = feed.feedLogObject.last_feed_given_date;
              mealId = feed.meal_id
              currentMeal++;
              feed.meal = currentMeal;
            }
          } else {
            previousDate = feed.feedLogObject.last_feed_given_date;
            mealId = feed.meal_id
            currentMeal = 1;
            feed.meal = currentMeal;
          }
        })
      })
      if (this.feedLogs.length == 0) {
        this.toasterService.warning('Meals not available');
        $('#addModal').modal('hide');
      } else {
        this.feedLogsData = this.feedLogs;
        $('#myModal').modal('show');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method convertToKgs
   * @description Convert grams to kilograms with 3 decimal places
   * @param {any} grams - Weight value in grams
   * @returns {string} Weight formatted as "X.XXX Kgs" (e.g., "1.500 Kgs")
   */
  convertToKgs = (grams) => {
    const kgsValue = grams / 1000;
    return kgsValue.toFixed(3) + ' Kgs';
  };

  /**
   * @method convertTo24Hrs
   * @description Convert 24-hour time format to 12-hour AM/PM format using Moment.js
   * @param {any} timeStamp - ISO 8601 timestamp string (e.g., "2026-01-19T14:30:00Z")
   * @returns {string} 12-hour formatted time (e.g., "2:30 PM")
   */
  convertTo24Hrs = (timeStamp) => {
    const time = timeStamp.split('T')[1];
    const _12hrs = moment(time, ['HH:mm']).format('h:mm A');
    return _12hrs;
  };

  /**
   * @method pageChange
   * @description Handle pagination page change event from pagination component
   *
   * Updates activePageNo to reflect current page selection in UI.
   * Note: This component uses infinite scroll via BatchLoader, so this is for UI state tracking only.
   *
   * @param {number} pageNum - Page number selected by user
   * @returns {void}
   * @side-effect Updates activePageNo property for pagination UI binding
   */
  public pageChange(pageNum: number) {
    this.activePageNo = pageNum;
  }
}
